import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientDeals, useClientParticipation, useTeamParticipation, useTeamUnlinked } from '@/api/m3';
import type { ClientDealFull, ParticipationRow, UnlinkedPerson } from '@/api/m3-types';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { Segments } from '@/components/kit/Layout';
import { CHECKLIST_LEGEND, Legend, StatusDot } from '@/components/kit/Marks';
import { EmptyState, ErrorState, Note, TableSkeleton } from '@/components/kit/Note';
import { Panel } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { date as ruDate, money } from '@/lib/format';
import { formatPhone } from '@/lib/phone';
import { CHECKLIST_COLUMNS } from '@/features/crm/deals/DealPage';
import { NewDealModal } from '@/features/crm/deals/DealDialogs';
import { idleText, READINESS, REG_STATUS, STAGES, stageTitle } from '@/features/crm/labels';

/**
 * Участие и сделки в карточках M2 (ТЗ M3 7.2, макет M2: regTable,
 * clientDeals, clientHist). Показываются, только когда включены флаги
 * deals и registrations.
 */

const dates = (from: string, to: string) => (from === to ? ruDate(from) : `${ruDate(from).slice(0, 5)} — ${ruDate(to)}`);
const regState = (r: { status: ParticipationRow['status']; readiness: ParticipationRow['readiness'] }) =>
  r.status === 'active' ? READINESS[r.readiness] : REG_STATUS[r.status];

/** «Текущее участие» команды (regTable в макете M2) */
export function CurrentParticipation({ teamId, onRegister }: { teamId: string; onRegister: () => void }) {
  const navigate = useNavigate();
  const data = useTeamParticipation(teamId, true);
  const rows = data.data?.current ?? [];
  return (
    <Panel
      flush
      title="Текущее участие"
      count={rows.length}
      actions={
        <Button small onClick={onRegister}>
          Зарегистрировать
        </Button>
      }
    >
      {data.error ? (
        <ErrorState message={data.error.message} onRetry={() => void data.refetch()} />
      ) : data.isLoading ? (
        <TableSkeleton rows={2} cols={5} />
      ) : rows.length ? (
        <>
          <table className="tbl">
            <thead>
              <tr>
                <th>Чемпионат и номинация</th>
                {CHECKLIST_COLUMNS.map((c) => (
                  <th key={c.key} className="rc">
                    {c.label}
                  </th>
                ))}
                <th className="r">Уч.</th>
                <th className="r">Сумма</th>
                <th>Регистрация</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = regState(r);
                return (
                  <tr key={r.id} className="clk" onClick={() => navigate(`/registrations/${r.id}`)}>
                    <td data-col="primary">
                      <div className="nm">{r.event.city}</div>
                      <div className="sub">
                        {r.nomination.name} · {dates(r.event.dateFrom, r.event.dateTo)}
                      </div>
                      <div className="sub">
                        сделка:{' '}
                        <span
                          className="lnk"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/crm/deals/${r.deal.id}`);
                          }}
                        >
                          {stageTitle(r.deal.stage).toLowerCase()}
                        </span>
                      </div>
                    </td>
                    {CHECKLIST_COLUMNS.map((c) => (
                      <td key={c.key} className="rc" data-col="hide">
                        <StatusDot state={r.checklist[c.key]} />
                      </td>
                    ))}
                    <td className="r num muted" data-col="hide">
                      {r.liveRows}
                    </td>
                    <td className="r" data-col="hide">
                      {r.status === 'active' ? (
                        <>
                          <div className="num">{money(r.billed)}</div>
                          <div className="sub">{r.billed && r.paid >= r.billed ? 'оплачено' : `оплачено ${money(r.paid)}`}</div>
                        </>
                      ) : (
                        <span className="na">—</span>
                      )}
                    </td>
                    <td data-col="secondary">
                      <Pill tone={st.tone}>{st.label}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Legend items={CHECKLIST_LEGEND} />
        </>
      ) : (
        <EmptyState>Сейчас команда никуда не заявлена.</EmptyState>
      )}
    </Panel>
  );
}

/** «История участия» команды — номера на прошедших мероприятиях */
export function TeamHistory({ teamId }: { teamId: string }) {
  const navigate = useNavigate();
  const data = useTeamParticipation(teamId, true);
  const rows = data.data?.history ?? [];
  return (
    <Panel>
      {data.error ? (
        <ErrorState message={data.error.message} onRetry={() => void data.refetch()} />
      ) : data.isLoading ? (
        <TableSkeleton rows={3} cols={4} />
      ) : rows.length ? (
        <>
          <table className="tbl">
            <thead>
              <tr>
                <th className="c-hev srt">
                  Чемпионат <span className="ar">▼</span>
                </th>
                <th>Номинация</th>
                <th>Итог</th>
                <th className="r">Уч.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="clk" onClick={() => navigate(`/registrations/${r.id}`)}>
                  <td data-col="primary">
                    <div className="nm">YouChamp · {r.event.city}</div>
                    <div className="sub">{dates(r.event.dateFrom, r.event.dateTo)}</div>
                  </td>
                  <td data-col="hide">{r.nomination.name}</td>
                  <td data-col="secondary">{r.status === 'active' ? 'выступили' : REG_STATUS[r.status].label.toLowerCase()}</td>
                  <td className="r num muted" data-col="hide">
                    {r.liveRows}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Note>Оценки судей по каждому выступлению появятся здесь вместе с модулем судейства.</Note>
        </>
      ) : (
        <EmptyState>Команда ещё не выступала на YouChamp.</EmptyState>
      )}
    </Panel>
  );
}

/**
 * «Выступали без карточки» — люди, вписанные в номера команды руками.
 * «Завести клиентом» открывает включение в состав с ФИО, датой и телефоном
 * из строки номера (Road Map п. 18).
 */
export function UnlinkedPeople({ teamId, onPromote }: { teamId: string; onPromote: (p: UnlinkedPerson) => void }) {
  const navigate = useNavigate();
  const data = useTeamUnlinked(teamId, true);
  if (data.error) return <ErrorState message={data.error.message} onRetry={() => void data.refetch()} />;
  if (data.isLoading || !data.data) return <TableSkeleton rows={3} cols={3} />;
  if (!data.data.length) return <EmptyState>Все участники номеров команды — клиенты из базы.</EmptyState>;
  return (
    <>
      <table className="tbl">
        <thead>
          <tr>
            <th>Участник</th>
            <th>Дата рождения</th>
            <th>Телефон</th>
            <th>Номера</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.data.map((p) => (
            <tr key={`${p.name}|${p.birthDate}`}>
              <td data-col="primary">
                <span className="nm">{p.name}</span>
              </td>
              <td className="num">{p.birthDate ? ruDate(p.birthDate) : <span className="na">—</span>}</td>
              <td className="num">{p.phone ? formatPhone(p.phone) : <span className="na">—</span>}</td>
              <td>
                {p.items.map((i) => (
                  <div key={i.registrationId} className="lnk" onClick={() => navigate(`/registrations/${i.registrationId}`)}>
                    {i.event} · {i.nomination}
                  </div>
                ))}
              </td>
              <td className="r" data-col="act">
                <TextLink onClick={() => onPromote(p)}>Завести клиентом</TextLink>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Note>
        Эти люди вписаны в номера фамилией и датой, без карточки клиента. «Завести клиентом» включит человека в состав
        с данными из номера — потом в номере строку можно будет связать с клиентом.
      </Note>
    </>
  );
}

/** «История участия» клиента: где выступал и кого выводил (clientHist в макете M2) */
export function ClientHistory({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const data = useClientParticipation(clientId, true);
  const rows = data.data?.history ?? [];
  return (
    <Panel>
      {data.error ? (
        <ErrorState message={data.error.message} onRetry={() => void data.refetch()} />
      ) : data.isLoading ? (
        <TableSkeleton rows={3} cols={5} />
      ) : rows.length ? (
        <table className="tbl">
          <thead>
            <tr>
              <th className="c-hev srt">
                Чемпионат <span className="ar">▼</span>
              </th>
              <th>Команда</th>
              <th>Номинация</th>
              <th>Роль</th>
              <th>Итог</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="clk" onClick={() => navigate(`/registrations/${r.id}`)}>
                <td data-col="primary">
                  <div className="nm">YouChamp · {r.event.city}</div>
                  <div className="sub">{dates(r.event.dateFrom, r.event.dateTo)}</div>
                </td>
                <td data-col="hide">{r.who}</td>
                <td data-col="hide">{r.nomination.name}</td>
                <td className="muted" data-col="hide">
                  {r.role}
                </td>
                <td data-col="secondary">{r.status === 'active' ? 'выступили' : REG_STATUS[r.status].label.toLowerCase()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState>Клиент ещё не выступал и не выводил команды на YouChamp.</EmptyState>
      )}
    </Panel>
  );
}

/**
 * Вкладка «Сделки» карточки клиента (clientDeals в макете M2, БЛ 11.2):
 * в работе — сделки с номерами-подстроками, завершённые — итогом.
 */
export function ClientDeals({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const data = useClientDeals(clientId);
  const [seg, setSeg] = useState<'open' | 'done'>('open');
  const [creating, setCreating] = useState(false);
  const all = data.data ?? [];
  const open = all.filter((d) => d.open);
  const done = all.filter((d) => !d.open).sort((a, b) => b.event.dateFrom.localeCompare(a.event.dateFrom));
  const step = (d: ClientDealFull) => STAGES.findIndex((s) => s.key === d.stage) + 1;

  const head = (
    <div className="segbar">
      <Segments
        value={seg}
        onChange={setSeg}
        items={[
          { value: 'open', label: 'В работе' },
          { value: 'done', label: 'Завершённые' },
        ]}
      />
      <div className="sp" />
      <Button onClick={() => setCreating(true)}>Новая сделка</Button>
    </div>
  );

  let body;
  if (data.error) body = <ErrorState message={data.error.message} onRetry={() => void data.refetch()} />;
  else if (data.isLoading) body = <TableSkeleton rows={3} cols={5} />;
  else if (seg === 'open') {
    body = open.length ? (
      <>
        <table className="tbl dtbl">
          <thead>
            <tr>
              <th className="c-dname">Сделка · регистрации</th>
              <th className="c-dst">Стадия · статус</th>
              {CHECKLIST_COLUMNS.map((c) => (
                <th key={c.key} className="rc">
                  {c.label}
                </th>
              ))}
              <th className="r">Уч.</th>
              <th className="r">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {open.map((d) => {
              const men = d.registrations.filter((r) => r.status === 'active').reduce((a, r) => a + r.liveRows, 0);
              return [
                <tr key={d.id} className="clk drow" onClick={() => navigate(`/crm/deals/${d.id}`)}>
                  <td data-col="primary">
                    <div className="nm">
                      {d.event.city} <span className="muted">· {dates(d.event.dateFrom, d.event.dateTo)}</span>
                    </div>
                    <div className="sub">
                      {d.owner?.fullName ?? 'ответственный не назначен'} · без движения {idleText(d.idleDays)}
                    </div>
                  </td>
                  <td data-col="secondary">
                    <div className="stg">{stageTitle(d.stage)}</div>
                    <div className="sub">
                      этап {step(d)} из {STAGES.length}
                      {step(d) >= 3 ? ' · ставится сам' : ''}
                    </div>
                  </td>
                  <td colSpan={CHECKLIST_COLUMNS.length} data-col="hide" />
                  <td className="r num muted" data-col="hide">
                    {men || ''}
                  </td>
                  <td className="r" data-col="hide">
                    {d.registrations.length ? (
                      <>
                        <div className="num">{money(d.plan)}</div>
                        <div className="sub">{d.plan && d.fact >= d.plan ? 'оплачено' : `оплачено ${money(d.fact)}`}</div>
                      </>
                    ) : d.forecast ? (
                      <>
                        <div className="num muted">{money(d.forecast)}</div>
                        <div className="sub">прогноз</div>
                      </>
                    ) : null}
                  </td>
                </tr>,
                ...(d.registrations.length
                  ? d.registrations.map((r) => {
                      const st = regState(r);
                      return (
                        <tr key={r.id} className="clk rrow" onClick={() => navigate(`/registrations/${r.id}`)}>
                          <td data-col="primary">
                            <div className="ind">
                              <span className="nm">{r.who}</span>
                              <div className="sub">{r.nomination.name}</div>
                            </div>
                          </td>
                          <td data-col="secondary">
                            <Pill tone={st.tone}>{st.label}</Pill>
                          </td>
                          {CHECKLIST_COLUMNS.map((c) => (
                            <td key={c.key} className="rc" data-col="hide">
                              <StatusDot state={r.status === 'declined' || r.status === 'moved' ? 'na' : r.checklist[c.key]} />
                            </td>
                          ))}
                          <td className="r num muted" data-col="hide">
                            {r.liveRows}
                          </td>
                          <td className="r num" data-col="hide">
                            {r.status === 'active' ? money(r.billed) : <span className="na">—</span>}
                          </td>
                        </tr>
                      );
                    })
                  : [
                      <tr key={`${d.id}:none`} className="rrow">
                        <td colSpan={CHECKLIST_COLUMNS.length + 4}>
                          <div className="ind sub">Регистраций пока нет — сделка на этапе продажи.</div>
                        </td>
                      </tr>,
                    ]),
              ];
            })}
          </tbody>
        </table>
        {open.some((d) => d.registrations.length) ? <Legend items={CHECKLIST_LEGEND} /> : null}
      </>
    ) : (
      <EmptyState>Сделок в работе нет.{done.length ? ' Прошлые — в «Завершённых».' : ''}</EmptyState>
    );
  } else {
    body = done.length ? (
      <table className="tbl">
        <thead>
          <tr>
            <th className="c-dname srt">
              Сделка <span className="ar">▼</span>
            </th>
            <th className="c-dres">Итог</th>
            <th>Команды</th>
            <th className="r">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {done.map((d) => (
            <tr key={d.id} className="clk" onClick={() => navigate(`/crm/deals/${d.id}`)}>
              <td data-col="primary">
                <div className="nm">
                  {d.event.city} <span className="muted">· {dates(d.event.dateFrom, d.event.dateTo)}</span>
                </div>
                <div className="sub">
                  {d.owner?.fullName ?? 'ответственный не назначен'} · сделка с {ruDate(d.createdAt.slice(0, 10))}
                </div>
              </td>
              <td data-col="secondary">
                {d.stage === 'lost' ? (
                  <>
                    <div>Проиграна</div>
                    <div className="sub">{d.lostReason}</div>
                  </>
                ) : (
                  <div>{stageTitle(d.stage)}</div>
                )}
              </td>
              <td data-col="hide">
                {d.registrations.filter((r) => r.status === 'active').map((r) => (
                  <div key={r.id}>
                    {r.who} <span className="muted">· {r.nomination.name}</span>
                  </div>
                ))}
                {!d.registrations.some((r) => r.status === 'active') ? <span className="dim">не выступали</span> : null}
              </td>
              <td className="r" data-col="hide">
                {d.plan ? (
                  <span className="num">{money(d.plan)}</span>
                ) : (
                  <>
                    <span className="num dim">{money(d.forecast ?? 0)}</span>
                    <div className="sub">не продали</div>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <EmptyState>Завершённых сделок нет.</EmptyState>
    );
  }

  return (
    <Panel>
      {head}
      {body}
      {creating ? <NewDealModal clientId={clientId} onClose={() => setCreating(false)} /> : null}
    </Panel>
  );
}
